import React from 'react'
import { ABV, Description, Style, SRM, Availability } from '../Highlights'
import {
    IBU,
    PrimaryFlavorNotes,
    HintsOfFlavorNotes,
    FoodPairings,
    ServingTemperature,
    ServingVessels,
} from './index'
import { Ingredients, KeyIngredients } from '../Ingredients'
import { useBrewDetailStore } from '../../state/useBrewDetailStore'

import Field from '../Field'
import * as UI from '@brewlogix/blx-ui'
import { parseEnumLabel } from 'src/util/helpers'

const Details: React.FC = () => {
    const {
        description,
        productData,
        hintsOfFlavorNotes,
        tastingExperience,
        perceivedBitterness,
        traits,
        perceivedSweetness,
        ibu,
        sourness,
        appearance,
        acidity,
        teaCharacter,
        carbonation,
        brixCount,
        body,
        probiotics,
        tannin,
        releaseDate,
        otherTraits,
    } = useBrewDetailStore()
    const anyDescription = description || productData?.style?.description
    const anyHintsOfFlavorNotes =
        hintsOfFlavorNotes?.length || productData?.style?.hintsOfFlavorNotes?.length
    const styleIbu =
        productData?.style?.ibuMin &&
        productData?.style?.ibuMax &&
        `${productData?.style?.ibuMin} - ${productData?.style?.ibuMax}`
    const anyIbu = ibu || styleIbu
    const isBeer = productData?.__typename === 'Beer'
    const isKombucha = productData?.__typename === 'Kombucha'
    const isCider = productData?.__typename === 'Cider'

    const carbonationValue = !!((isKombucha || isCider) && carbonation)
    const brixValue = !!((isKombucha || isCider) && brixCount)
    const bodyValue = !!((isKombucha || isCider) && body)

    return (
        <div className='gap-9 flex flex-col h-full mt-8'>
            <div className='gap-9 grid grid-cols-2'>
                <div className='gap-9 lg:hidden grid grid-cols-2 col-span-2'>
                    <div className='col-span-1'>
                        <Style />
                    </div>

                    <div className='col-span-1'>
                        <ABV />
                    </div>

                    <div className='col-span-1'>
                        <SRM />
                    </div>
                    <div className='col-span-1'>
                        <Availability />
                    </div>
                    {anyDescription && (
                        <div className='col-span-2'>
                            <Description />
                        </div>
                    )}
                </div>

                <PrimaryFlavorNotes />
                {anyHintsOfFlavorNotes && <HintsOfFlavorNotes />}
                <FoodPairings />
                <ServingTemperature />
                <ServingVessels />
                {traits?.allergens?.length > 0 && (
                    <Field title='Allergens/Indicators' gap='2'>
                        {traits?.allergens?.map((item) => (
                            <UI.Pill
                                key={item}
                                label={item}
                                customClasses='bg-gray-100 text-base font-normal py-1 px-3 text-gray-500'
                            />
                        ))}
                    </Field>
                )}
                {perceivedBitterness && isBeer && (
                    <Field colSpan='2' title='Perceived Bitterness'>
                        {perceivedBitterness}
                    </Field>
                )}
                {anyIbu && isBeer && <IBU />}
                {tastingExperience && (
                    <Field colSpan='2' title='Tasting Notes'>
                        {tastingExperience}
                    </Field>
                )}
                {isBeer && <Ingredients />}
                {isKombucha && sourness && (
                    <Field title='Sourness'>
                        <div className='text-lg font-bold'>{sourness}</div>
                    </Field>
                )}
                {(isKombucha || isCider) && perceivedSweetness && (
                    <Field title='Perceived Sweetness'>
                        <div className='text-lg font-bold'>{perceivedSweetness}</div>
                    </Field>
                )}
                {isKombucha && appearance && (
                    <Field title='Appearance'>
                        <div className='text-lg font-bold'>{appearance}</div>
                    </Field>
                )}
                {(isKombucha || isCider) && acidity && (
                    <Field title='Acidity'>
                        <div className='text-lg font-bold'>{acidity}</div>
                    </Field>
                )}
                {isKombucha && teaCharacter && (
                    <Field title='Tea Character'>
                        <div className='text-lg font-bold'>{teaCharacter}</div>
                    </Field>
                )}
                {carbonationValue && (
                    <Field title='Carbonation'>
                        <div className='text-lg font-bold'>{carbonation}</div>
                    </Field>
                )}
                {brixValue && (
                    <Field title='Brix Count'>
                        <div className='text-lg font-bold'>{brixCount}%</div>
                    </Field>
                )}
                {bodyValue && (
                    <Field title='Body'>
                        <div className='text-lg font-bold'>{body}</div>
                    </Field>
                )}
                {isKombucha && probiotics && (
                    <Field title='Probiotics' colSpan='2'>
                        {probiotics}
                    </Field>
                )}
                {isCider && (
                    <Field title='Tannin'>
                        <div className='text-lg font-bold'>{tannin}</div>
                    </Field>
                )}
                {(isKombucha || isCider) && KeyIngredients.length > 0 && <KeyIngredients />}
                {otherTraits?.length > 0 && (
                    <Field title='Traits' gap='2'>
                        {otherTraits?.map((item) => (
                            <UI.Pill
                                key={item}
                                label={parseEnumLabel(item)}
                                customClasses='bg-gray-100 text-base font-normal py-1 px-3 text-gray-500'
                            />
                        ))}
                    </Field>
                )}
                {isKombucha && productData?.releaseDate && (
                    <Field title='Release Year'>
                        <div className='text-lg font-bold'>{releaseDate}</div>
                    </Field>
                )}
            </div>
        </div>
    )
}

export default React.memo(Details)
